/**
 * @module utils/datePresets
 * @description Named periods ("This week", "Last month", …) and the dates they
 * stand for — ONE definition for every date-range filter in the app
 * (Maurizio, 2026-09-24: "date presets … should be added to all date range
 * filters — share the same functions — app-wide"). The DateRangeFilter
 * component renders them; pages only choose which periods to offer.
 *
 * Every period is counted in DUBAI calendar days, whatever the browser's
 * timezone (Maurizio, 2026-09-24: "be careful with the cross-day time … dubai
 * time, utc time"). Orders (visiera order_date), journal entries and stock
 * movements are all stored as Dubai dates; a laptop in Italy at 23:00 CEST is
 * already on the next day in Dubai, and browser-local "today" would pick the
 * wrong day. The Dubai day is read with Intl (Asia/Dubai, UTC+4, no DST) and
 * all arithmetic is done on UTC-midnight dates, so the browser's own DST
 * can never shift a day either.
 *
 * Weeks start on Monday. Periods that include today end today.
 */

/** The business timezone every date in the app is kept in. */
export const BUSINESS_TZ = "Asia/Dubai";

/** Today's Dubai calendar day, as a Date at 00:00 UTC of that day. */
export function businessDay(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: BUSINESS_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now);
  const get = (t) => Number(parts.find((p) => p.type === t).value);
  return new Date(Date.UTC(get("year"), get("month") - 1, get("day")));
}

/** YYYY-MM-DD of a UTC-midnight date. */
const f = (d) => d.toISOString().slice(0, 10);

/** Every period the app knows, keyed by value, with its menu text. */
export const PERIODS = {
  today: "Today",
  yesterday: "Yesterday",
  tomorrow: "Tomorrow",
  thisWeek: "This Week",
  lastWeek: "Last Week",
  last7: "Last 7 Days",
  last30: "Last 30 Days",
  thisMonth: "This Month",
  lastMonth: "Last Month",
  thisYear: "This Year",
  lastYear: "Last Year",
  // Working weeks, Monday–Friday whole (Fusion Eat plans ahead, so "this
  // week" includes the days still to come).
  lastWorkWeek: "Last Week (Mon–Fri)",
  thisWorkWeek: "This Week (Mon–Fri)",
  nextWorkWeek: "Next Week (Mon–Fri)",
  all: "All",
};

/** The standard set every date-range filter offers. */
export const DEFAULT_PERIODS = ["today", "yesterday", "thisWeek", "lastWeek", "thisMonth", "lastMonth", "thisYear", "lastYear"];

/** Menu items ({ text, value }) for a list of period keys, in the given order. */
export function periodItems(keys = DEFAULT_PERIODS) {
  return keys.filter((k) => PERIODS[k]).map((k) => ({ text: PERIODS[k], value: k }));
}

/** Kept for existing imports: the standard menu. */
export const PERIOD_PRESETS = periodItems(DEFAULT_PERIODS);

/** Monday of the week that contains `d` (UTC-midnight date). */
function mondayOf(d) {
  return addDays(d, -((d.getUTCDay() || 7) - 1));
}

function addDays(d, n) {
  const x = new Date(d);
  x.setUTCDate(x.getUTCDate() + n);
  return x;
}

/**
 * [from, to] as YYYY-MM-DD (Dubai days) for a period key; [null, null] for
 * "all" or an unknown key. `now` is an instant — any Date.
 */
export function periodRange(key, now = new Date()) {
  const today = businessDay(now);
  const y = today.getUTCFullYear();
  const mo = today.getUTCMonth();
  const day = (yy, mm, dd) => new Date(Date.UTC(yy, mm, dd));
  switch (key) {
    case "today":
      return [f(today), f(today)];
    case "yesterday":
      return [f(addDays(today, -1)), f(addDays(today, -1))];
    case "tomorrow":
      return [f(addDays(today, 1)), f(addDays(today, 1))];
    case "thisWeek":
      return [f(mondayOf(today)), f(today)];
    case "lastWeek": {
      const mon = addDays(mondayOf(today), -7);
      return [f(mon), f(addDays(mon, 6))];
    }
    case "last7":
      return [f(addDays(today, -6)), f(today)];
    case "last30":
      return [f(addDays(today, -29)), f(today)];
    case "thisMonth":
      return [f(day(y, mo, 1)), f(today)];
    case "lastMonth":
      return [f(day(y, mo - 1, 1)), f(day(y, mo, 0))];
    case "thisYear":
      return [f(day(y, 0, 1)), f(today)];
    case "lastYear":
      return [f(day(y - 1, 0, 1)), f(day(y - 1, 11, 31))];
    case "lastWorkWeek":
    case "thisWorkWeek":
    case "nextWorkWeek": {
      const offset = key === "lastWorkWeek" ? -7 : key === "nextWorkWeek" ? 7 : 0;
      const mon = addDays(mondayOf(today), offset);
      return [f(mon), f(addDays(mon, 4))];
    }
    default:
      return [null, null];
  }
}

/** YYYY-MM-DD `n` days after the YYYY-MM-DD `ymd` (n may be negative). */
export function addDaysYmd(ymd, n) {
  return f(addDays(new Date(`${ymd}T00:00:00Z`), n));
}

/** The period whose range is exactly [from, to] among `keys`, or null. */
export function periodMatching(from, to, keys = DEFAULT_PERIODS, now = new Date()) {
  return keys.find((k) => {
    const [a, b] = periodRange(k, now);
    return a === (from || null) && b === (to || null);
  }) ?? null;
}
